import React, { CSSProperties } from 'react';
import {
  Box,
  BoxProps,
  Theme,
  Typography,
  TypographyProps,
} from '@mui/material';
import classNames from 'classnames';
import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles((theme: Theme) => ({
  link: {
    color: theme.palette.secondary.main,
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.secondary.dark,
    },
  },
  disabled: {
    color: theme.palette.text.disabled,
    cursor: 'initial',
    '&:hover': {
      color: theme.palette.text.disabled,
    },
  },
  error: {
    color: 'var(--error-color-text-dark)',
    '&:hover': {
      color: 'var(--error-color-text-dark)',
    },
  },
}));

type PickedTypographyProps = Pick<
  TypographyProps,
  'onClick' | 'sx' | 'variant'
>;

interface Props extends PickedTypographyProps {
  afterIcon?: React.ReactNode;
  beforeIcon?: React.ReactNode;
  bold?: boolean;
  className?: string;
  color?: TypographyProps['color'];
  component?: React.ElementType;
  disabled?: boolean;
  disableInlineFlex?: boolean;
  error?: boolean;
  href?: string;
  iconWrapperStyle?: CSSProperties;
  text: React.ReactNode;
  textSx?: BoxProps['sx'];
  dataTestId?: string;
}

const Link: React.FC<Props> = (props: Props): JSX.Element => {
  const classes = useStyles();

  const {
    afterIcon,
    beforeIcon,
    bold = false,
    className,
    color,
    component,
    disabled = false,
    disableInlineFlex = false,
    error = false,
    href,
    iconWrapperStyle,
    onClick,
    sx,
    text,
    textSx,
    variant,
    dataTestId,
  } = props;

  const hasIcon = !!afterIcon || !!beforeIcon;

  const typographyComponent = component || (hasIcon ? 'div' : 'p');

  const linkClasses = classNames(
    classes.link,
    { [classes.disabled]: disabled, [classes.error]: error },
    className
  );

  let wrappedText = (
    <Box component="span" sx={textSx}>
      {text}
    </Box>
  );

  if (bold) {
    wrappedText = <Box component="strong">{text}</Box>;
  }

  let content = wrappedText;

  if (hasIcon) {
    content = (
      <Box
        alignItems="center"
        component="span"
        display={disableInlineFlex ? 'flex' : 'inline-flex'}
        style={iconWrapperStyle}
      >
        {beforeIcon}
        <Box component="span" pl={beforeIcon ? 1 : 0} pr={afterIcon ? 1 : 0}>
          {wrappedText}
        </Box>
        {afterIcon}
      </Box>
    );
  }

  if (href) {
    content = (
      <a
        className={linkClasses}
        href={href}
        rel="noopener noreferrer"
        target="_blank"
      >
        {content}
      </a>
    );
  }

  return (
    <Typography
      color={color}
      className={linkClasses}
      component={typographyComponent}
      onClick={onClick}
      sx={sx}
      variant={variant}
      data-testid={dataTestId}
    >
      {content}
    </Typography>
  );
};

export default Link;
